"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Lock } from "lucide-react";
import { GradText, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { NOT_REDUCED, useEnterStagger } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import LinkRow, { LinkSheet, RowChip } from "./LinkRow";

/** Auto-advance beat. Errs slow (playbook): a reader should finish the
 *  sentence before the sheet moves on. */
const STEP_MS = 5200;

type Step = {
  id: string;
  title: string;
  body: ReactNode;
  /** Header note for the sheet — what the row is demonstrating. */
  note: string;
  sheet: ReactNode;
  summary: string;
};

const link = (name: "dynamicResponses" | "smartRouting", label: string) => (
  <a
    href={routePath(name)}
    onClick={(e) => e.stopPropagation()}
    className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600"
  >
    {label}
  </a>
);

const Strip = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="mt-auto bg-surface-2/50 px-[18px] py-[12px]">
    <div className="font-jetbrains text-[10.5px] tracking-[0.12em] text-dim">{label}</div>
    <div className="mt-2 space-y-1.5 font-jetbrains text-[12px] text-muted">{children}</div>
  </div>
);

const Line = ({ what, when }: { what: ReactNode; when?: string }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="min-w-0 truncate">{what}</span>
    {when && <span className="shrink-0 text-dim">{when}</span>}
  </div>
);

const LIVE = (
  <span className="inline-flex items-center gap-1 text-data-green">
    <Check size={11} strokeWidth={3} aria-hidden /> live · just now
  </span>
);

/**
 * One step per basic. The left sheet always shows the same link — the step
 * decides which of its controls is being changed, so the page argues "same
 * link, more control" by demonstration rather than by four paragraphs.
 */
const STEPS: Step[] = [
  {
    id: "destination",
    title: "Change where it goes. Never the link.",
    body: "Edit the destination and it is live in the routing layer the moment you save — the same link on every poster, email and post.",
    note: "EDIT IN PLACE",
    summary: "The destination of go.example.com/summer changes from example.com/summer-2025 to example.com/summer-2026 and is live immediately.",
    sheet: (
      <>
        <LinkRow slug="go.example.com/summer" destination="example.com/summer-2025" meta="before" muted />
        <LinkRow slug="go.example.com/summer" destination="example.com/summer-2026" meta={LIVE} highlightDestination />
        <Strip label="ON SAVE">
          <Line what="destination ⟶ example.com/summer-2026" when="saved" />
          <Line what="routing layer serves the new destination" when="next request" />
          <Line what="posters, emails, posts keep the same link" />
        </Strip>
      </>
    ),
  },
  {
    id: "path",
    title: "Your path, your words",
    body: "The part after the domain is yours: /summer, /menu, /q4-launch. Renaming frees the old path — point a second link at the new one if the old URL is already out there.",
    note: "RENAME THE PATH",
    summary: "The path is renamed from /summer-sale to /summer; the old path is free again, and a second link can point it at the new one.",
    sheet: (
      <>
        <LinkRow slug="go.example.com/summer-sale" destination="example.com/summer-2026" meta="renamed" muted />
        <LinkRow slug="go.example.com/summer" destination="example.com/summer-2026" meta={LIVE} highlightSlug />
        <Strip label="OLD PATH">
          <Line what="/summer-sale is free again" when="on save" />
          <Line what="already printed? add /summer-sale ⟶ 301 ⟶ /summer" />
        </Strip>
      </>
    ),
  },
  {
    id: "code",
    title: "The redirect type is a setting",
    body: "301 when the move is permanent and search engines should follow. 302 for campaigns and seasons. 307 in front of an API, so a POST stays a POST.",
    note: "STATUS CODE",
    summary: "The redirect status code is a per-link setting: 301 permanent, 302 temporary, 307 method-preserving.",
    sheet: (
      <>
        <LinkRow slug="go.example.com/summer" destination="example.com/summer-2026" code={302} meta="seasonal" highlightCode />
        <LinkRow slug="go.example.com/docs" destination="docs.example.com" code={301} meta="moved for good" />
        <LinkRow slug="go.example.com/api/v1" destination="api.example.com/v1" code={307} meta="keeps POST" />
        <Strip label="WHEN">
          <Line what="301 · permanent — search engines move their ranking" />
          <Line what="302 · temporary — campaigns, seasons, tests" />
          <Line what="307 · method-preserving — redirects in front of APIs" />
        </Strip>
      </>
    ),
  },
  {
    id: "domain",
    title: "The visitor sees your domain",
    body: "The address they click and share is on your domain. The raw destination only appears once the redirect lands.",
    note: "WHAT IS SHARED",
    summary: "Visitors see and share go.example.com/summer; the destination example.com/summer-2026 only appears after the redirect.",
    sheet: (
      <>
        <LinkRow slug="go.example.com/summer" destination="example.com/summer-2026" meta={LIVE} />
        <div className="px-[18px] py-[14px]">
          <div className="font-jetbrains text-[10.5px] tracking-[0.12em] text-dim">IN THE ADDRESS BAR</div>
          <div className="mt-2 flex items-center gap-2 rounded-[9px] border border-line-2 bg-surface-2 px-3 py-2 font-jetbrains text-[12.5px] text-ink">
            <Lock size={11} className="shrink-0 text-data-green" aria-hidden />
            go.example.com/summer
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <RowChip>on a poster</RowChip>
            <RowChip>in a newsletter</RowChip>
            <RowChip>in a bio</RowChip>
          </div>
        </div>
        <Strip label="AFTER THE CLICK">
          <Line what="⟶ example.com/summer-2026" when="302" />
        </Strip>
      </>
    ),
  },
  {
    id: "answer",
    title: "Not every link has to redirect",
    body: (
      <>
        On Premium, a link can answer with JSON, HTML or a plain 200 straight from the link itself — {link("dynamicResponses", "dynamic responses")} — and mobile traffic can go straight into your app with {link("smartRouting", "smart routing")}.
      </>
    ),
    note: "ANSWER, DON'T REDIRECT",
    summary: "go.example.com/price answers a JSON body with a 200 status instead of redirecting, for every client; go.example.com/pricing stays a 302 redirect.",
    sheet: (
      <>
        <LinkRow
          slug="go.example.com/price"
          destination={<span className="text-ink">{'{ "price": 19, "currency": "EUR" }'}</span>}
          code={200}
          meta="application/json"
          highlightCode
        />
        <LinkRow slug="go.example.com/pricing" destination="example.com/pricing" code={302} meta="redirect" />
        <Strip label="ANY CLIENT">
          <Line what="GET /price ⟶ 200 · application/json · the body above" />
          <Line what="GET /pricing ⟶ 302 ⟶ example.com/pricing" />
        </Strip>
      </>
    ),
  },
];

/**
 * Control the destination — the most basic promise a shortener makes, shown
 * as a five-step walk-through. The right column is a stepper: one step open
 * at a time, the others folded to their title; the left sheet is the same
 * link wearing whatever the open step changes. Auto-advances on a slow beat
 * while in view, pauses on hover, and hands over for good on the first
 * click. Reduced motion and phones get no beat — the reader clicks through.
 */
export default function DestinationSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-rise]", { y: 22, stagger: 0.08 });

  const [active, setActive] = useState(0);
  // One step is open at a time in every mode — the server renders the same
  // shape the client settles into, so nothing jumps on hydration and the
  // whole-row measurement is right the first time. static: no beat (reduced
  // motion, phones, before mount) — the reader clicks through. auto: advancing
  // on the beat. manual: the reader clicked, the beat stops for good.
  const [mode, setMode] = useState<"static" | "auto" | "manual">("static");
  const auto = mode === "auto";
  const [hover, setHover] = useState(false);
  const [beat, setBeat] = useState(0);
  const inView = useRef(false);

  // Auto-advance only when motion is allowed, on md+, and the block is on
  // screen; a click hands control to the reader permanently.
  useEffect(() => {
    const mm = window.matchMedia(`${NOT_REDUCED} and (min-width: 768px)`);
    if (!mm.matches) return;
    setMode("auto");
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (inView.current = e.isIntersecting)),
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!auto) return;
    const id = window.setInterval(() => {
      if (!inView.current || hover || document.hidden) return;
      setActive((a) => (a + 1) % STEPS.length);
      setBeat((b) => b + 1);
    }, STEP_MS);
    return () => window.clearInterval(id);
  }, [auto, hover]);

  const pick = (i: number) => {
    setActive(i);
    setMode("manual");
  };

  const step = STEPS[active];

  return (
    <SectionShell id="destination" className="scroll-mt-16">
      <SectionHead
        eyebrow="Control the destination"
        title={
          <>
            Change where it goes. <GradText>Never the link.</GradText>
          </>
        }
        lead="Every edit — a new destination, a different status code, a renamed path — reaches the routing layer the moment you save. Not a nightly sync: the same Redis engine that answers every request sees your change immediately."
      />

      <div ref={rootRef} className="flex flex-col">
        {/* Sheet takes eight cells, one open cell, the stepper the rest. The
            sheet is pinned to five rows on lg+ so a step swap never changes
            the block height (and the clearing below never drifts); the
            stepper stretches to the same height and centers its list. */}
        <div
          data-grid-block
          className="grid items-stretch gap-y-10 lg:grid-cols-[calc(var(--grid-cell,72px)*8)_minmax(0,1fr)] lg:gap-x-[var(--grid-cell,72px)]"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <div data-rise className="flex min-w-0 flex-col">
            <p className="sr-only">{step.summary}</p>
            <div aria-hidden="true" className="flex flex-col lg:h-[calc(var(--grid-cell,72px)*5)]">
              {/* Keyed on the step so the swap re-runs its settle. */}
              <div key={step.id} className="lm-swap flex flex-1 flex-col">
                <LinkSheet label="LINKS · GO.EXAMPLE.COM" note={step.note} fill>
                  {step.sheet}
                </LinkSheet>
              </div>
            </div>
          </div>

          <ol className="flex flex-col justify-center" aria-label="What you control">
            {STEPS.map((s, i) => {
              const open = i === active;
              return (
                <li key={s.id} className="border-t border-line first:border-t-0">
                  <button
                    type="button"
                    onClick={() => pick(i)}
                    aria-expanded={open}
                    className="group flex w-full items-start gap-3 py-[11px] text-left"
                  >
                    <span
                      aria-hidden
                      className={`mt-[7px] h-[7px] w-[7px] shrink-0 rounded-full transition-colors ${open ? "bg-brand-600" : "bg-line-3 group-hover:bg-brand-600/50"}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[15.5px] font-bold tracking-[-0.01em] transition-colors ${open ? "text-ink" : "text-muted group-hover:text-ink"}`}>
                        {s.title}
                      </span>
                      {/* The body simply isn't in the box when folded; the
                          open one settles in with the same short rise the
                          sheet uses, keyed so a re-open replays it. */}
                      {open && (
                        <span key={`${s.id}-${active}`} id={`lm-step-${s.id}`} className="lm-swap block pt-1 text-[14px] leading-[1.55] text-muted">
                          {s.body}
                        </span>
                      )}
                    </span>
                  </button>
                  {open && auto && (
                    <div aria-hidden className="ml-[19px] h-px bg-line">
                      <div
                        key={beat}
                        data-paused={hover || undefined}
                        className="lm-progress h-px bg-brand-600/60"
                        style={{ ["--lm-step" as string]: `${STEP_MS}ms` }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </SectionShell>
  );
}
